import React, { useState } from "react";
import { useGame } from "../../contexts/GameContext";
import { ITEMS, ITEM_RARITY_COLORS } from "../../data/items";
import { Backpack, X } from "lucide-react";
import { toast } from "sonner";
import { SFX } from "../../utils/audio";

export const InventoryModal = ({ open, onClose, inCombat = false, onUseInCombat }) => {
  const { character, useInventoryItem: gameUseItem } = useGame();
  const [busy, setBusy] = useState(false);

  if (!open || !character) return null;
  const inv = character.inventory || [];

  const handleUse = (entry) => {
    if (busy) return;
    setBusy(true);
    const item = ITEMS[entry.id];
    if (!item) { setBusy(false); return; }

    // Si on est en combat et l'item est combat-only → délègue au handler combat
    if (inCombat && !item.everywhere && item.inCombat && onUseInCombat) {
      const used = onUseInCombat(entry, item);
      if (used) {
        SFX.buy();
        toast.success(`${item.nom} utilisé !`, { icon: item.icon, duration: 2000 });
        onClose?.();
      }
      setBusy(false);
      return;
    }
    // Hors combat → effet permanent / heal direct
    if (!item.everywhere) {
      toast.error("Utilisable uniquement en combat", { icon: item.icon });
      setBusy(false);
      return;
    }
    const ok = gameUseItem(entry.uuid);
    if (ok) {
      SFX.buy();
      toast.success(`${item.nom} utilisé !`, {
        description: item.description,
        icon: item.icon,
        duration: 2500,
      });
      // Close if last item, else keep open
      if (inv.length <= 1) onClose?.();
    }
    setBusy(false);
  };

  return (
    <div data-testid="inventory-modal" className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-5 cartoon-shadow-lg-static max-w-2xl w-full relative" onClick={(e) => e.stopPropagation()}>
        <button data-testid="close-inventory" onClick={onClose}
          className="absolute -top-2 -right-2 bg-rose-500 text-white border-4 border-slate-950 rounded-full w-9 h-9 flex items-center justify-center cartoon-shadow">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-4">
          <Backpack className="w-7 h-7 text-yellow-300" />
          <h2 className="font-display text-2xl font-bold">Inventaire</h2>
          <span className="text-sm text-slate-400 ml-2">{inv.length} objet{inv.length !== 1 ? "s" : ""}</span>
        </div>

        {inv.length === 0 ? (
          <p className="text-center text-slate-400 py-8">Aucun objet. Trouve-en dans les coffres et boutiques !</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto">
            {inv.map((entry) => {
              const item = ITEMS[entry.id];
              if (!item) return null;
              const rarityBorder = ITEM_RARITY_COLORS[item.rarity] || ITEM_RARITY_COLORS.common;
              const usable = inCombat ? (item.inCombat || item.everywhere) : item.everywhere;
              return (
                <div key={entry.uuid} data-testid={`inventory-item-${item.id}`}
                  className={`bg-slate-900 border-4 ${rarityBorder} rounded-2xl p-3 ${!usable ? "opacity-50" : ""}`}>
                  <div className={`${item.color} border-2 border-slate-950 rounded-xl h-16 flex items-center justify-center text-3xl mb-2`}>
                    {item.icon}
                  </div>
                  <h4 className="font-display font-bold text-sm leading-tight">{item.nom}</h4>
                  <p className="text-[11px] text-slate-300 mb-2 leading-tight">{item.description}</p>
                  <button
                    data-testid={`use-item-${item.id}`}
                    disabled={!usable || busy}
                    onClick={() => handleUse(entry)}
                    className={`w-full ${usable ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-400"} border-2 border-slate-950 rounded-xl py-1.5 font-display font-bold uppercase text-xs cartoon-shadow disabled:cursor-not-allowed`}
                  >
                    {usable ? "Utiliser" : (inCombat ? "Hors combat" : "En combat seulement")}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
